import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Select, { SelectOption } from '@amzn/meridian/select';
import Input from '@amzn/meridian/input';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Loader from '@amzn/meridian/loader';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import DataTable, {
   DataTableBody,
   DataTableHeader,
   DataTableHead,
   DataTableCell,
   DataTableRow,
} from '@amzn/meridian/data-table';
import Divider from '@amzn/meridian/divider';
import Pagination from '@amzn/meridian/pagination';
import SearchField from '@amzn/meridian/search-field';
import Toggle from '@amzn/meridian/toggle';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import { getDSData, getNodeConfig, bulkUpdateDSData } from '../services/api';
import { DS_AMZL_INPUT_TABLE, NODE_CONFIG_TABLE } from '../appConfig';
import { dayAfterTomorrow, ofdDateOptions, tomorrow } from '../utils/dateUtils';
import { downloadCSV } from '../utils/csvUtils';
import { BACKLOG_REASONS } from '../constants/backlogReasons';
import TacticalCaps from './TacticalCaps';

interface DSInputItem {
   ofd_date: string;
   location_id: string;
   cycle_name: string;
   utr: string;
   mnr: string;
   source: string;
   cycle_transfer: string;
   backlog: string;
   backlog_reason: string;
   earlies: string;
   uncapped_flag: string;
   last_updated_by: string;
   last_updated_at?: string;
   parent_location?: string;
}

const AMZLDSInputs: React.FC = () => {
   const [activeTab, setActiveTab] = useState<string>('cap-setting-inputs');
   const [data, setData] = useState<DSInputItem[]>([]);
   const [nodeConfig, setNodeConfig] = useState<any[]>([]);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [selectedDate, setSelectedDate] = useState<string>('');
   const [selectedParentNode, setSelectedParentNode] = useState<string>('');
   const [filterText, setFilterText] = useState('');
   const [currentPage, setCurrentPage] = useState(1);
   const [resultsPerPage, setResultsPerPage] = useState(20);
   const [sortColumn, setSortColumn] = useState('ofd_date');
   const [sortDirection, setSortDirection] = useState<'ascending' | 'descending'>('ascending');
   const [originalEditData, setOriginalEditData] = useState<DSInputItem[]>([]);


   // Modal state
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [editData, setEditData] = useState<DSInputItem[]>([]);
   const [isSaving, setIsSaving] = useState(false);

   useEffect(() => { loadData(); }, []);
   useEffect(() => { setCurrentPage(1); }, [selectedDate, selectedParentNode, filterText, resultsPerPage]);

   const loadData = useCallback(async () => {
       setIsLoading(true);
       setError(null);
       try {
           const [dsResult, configResult] = await Promise.all([
               getDSData(DS_AMZL_INPUT_TABLE, {
                    filters: [
                        { key: 'ofd_date', operator: 'in', values: [tomorrow(), dayAfterTomorrow()] },
                    ],
                }),
               getNodeConfig(NODE_CONFIG_TABLE),
           ]);
           setData(Array.isArray(dsResult?.data || dsResult?.items || dsResult) ? (dsResult?.data || dsResult?.items || dsResult) : []);
           setNodeConfig(Array.isArray(configResult?.items || configResult) ? (configResult?.items || configResult) : []);
       } catch (err: any) {
           setError(err.message || 'Failed to load data');
       } finally {
           setIsLoading(false);
       }
   }, []);

   const dateOptions = useMemo(() => ofdDateOptions(), []);

    const parentNodeOptions = useMemo(() => {
        const amzlNodes = nodeConfig.filter((item: any) => item.type === 'AMZL');
        return Array.from(new Set(amzlNodes.map((item: any) => item.parent))).filter(Boolean).sort() as string[];
    }, [nodeConfig]);

   const filteredData = useMemo(() => {
       const parentMap = new Map(nodeConfig.map((item: any) => [item.location_id, item.parent]));
       const childNodesOfParent = selectedParentNode
           ? new Set(nodeConfig.filter((item: any) => item.parent === selectedParentNode).map((item: any) => item.location_id))
           : null;
       let result = data.map(item => ({
           ...item,
           parent_location: parentMap.get(item.location_id) || '',
       }));
       if (selectedDate) result = result.filter(item => item.ofd_date === selectedDate);
       if (selectedParentNode && childNodesOfParent) {
           result = result.filter(item => childNodesOfParent.has(item.location_id) || item.location_id === selectedParentNode);
       }
       if (filterText) {
           const lower = filterText.toLowerCase();
           result = result.filter(item =>
               item.location_id?.toLowerCase().includes(lower) ||
               item.cycle_name?.toLowerCase().includes(lower)
           );
       }
       result.sort((a, b) => {
           const aVal = (a as any)[sortColumn] || '';
           const bVal = (b as any)[sortColumn] || '';
           const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
           return sortDirection === 'ascending' ? cmp : -cmp;
       });
       return result;
   }, [data, nodeConfig, selectedDate, selectedParentNode, filterText, sortColumn, sortDirection]);

   const totalPages = Math.max(1, Math.ceil(filteredData.length / resultsPerPage));
   const paginatedData = filteredData.slice((currentPage - 1) * resultsPerPage, currentPage * resultsPerPage);

   // Row key helper
   const getRowKey = (item: DSInputItem) => `${item.ofd_date}-${item.location_id}-${item.cycle_name}`;

    const onSort = useCallback(({ sortColumn: col, sortDirection: dir }: any) => {
        setSortColumn(col);
        setSortDirection(dir);
    }, []);

   // Modal handlers
   const openEditModal = () => {
       setEditData(filteredData.map(r => ({ ...r })));
       setOriginalEditData(filteredData.map(r => ({ ...r })));
       setIsModalOpen(true);
   };

   const handleModalEdit = (idx: number, field: string, value: string) => {
       setEditData(prev => {
           const updated = [...prev];
           updated[idx] = { ...updated[idx], [field]: value };
           return updated;
       });
   };

   const getModalValidationError = (item: DSInputItem) => {
       const errors: Record<string, boolean> = {};
       if (item.cycle_name === 'CYCLE_1') {
           if (item.mnr && item.mnr !== '0' && !item.source?.trim()) errors.source = true;
           if (item.backlog && item.backlog !== '0' && !item.backlog_reason?.trim()) errors.backlog_reason = true;
       }
       return errors;
   };

   const handleSaveAll = async () => {
       const changedRows = editData.filter((row, idx) => {
           const orig = originalEditData[idx];
           if (!orig) return true;
           return ['utr', 'mnr', 'source', 'backlog', 'backlog_reason', 'cycle_transfer', 'earlies', 'uncapped_flag']
               .some(field => String(row[field as keyof DSInputItem] ?? '') !== String(orig[field as keyof DSInputItem] ?? ''));
       });

       if (changedRows.length === 0) {
           setIsModalOpen(false);
           return;
       }

       setIsSaving(true);
       try {
           const records = changedRows.map(row => ({
               ofd_date: row.ofd_date,
               location_id: row.location_id,
               cycle_name: row.cycle_name,
               updates: {
                    utr: row.utr,
                    mnr: row.mnr,
                    source: row.source,
                    backlog: row.backlog,
                    backlog_reason: row.backlog_reason,
                    cycle_transfer: row.cycle_transfer,
                    uncapped_flag: row.uncapped_flag,
                    earlies: row.earlies,
                },
           }));
           await bulkUpdateDSData(DS_AMZL_INPUT_TABLE, records);

           setIsModalOpen(false);
           await loadData();
       } catch (err: any) {
           setError(err.message || 'Failed to save');
       } finally {
           setIsSaving(false);
       }
   };

   const getCellValue = (item: any, colId: string): string => {
       switch (colId) {
           case 'mnr':
               return item.cycle_name === 'CYCLE_1' ? (item.mnr || '') : '';
           case 'backlog':
               return item.cycle_name === 'CYCLE_1' ? (item.backlog || '') : '';
           case 'backlog_reason':
               return item.cycle_name === 'CYCLE_1' ? (item.backlog_reason || '') : '';
           case 'uncapped_flag':
               return item.uncapped_flag === '1' ? 'Uncapped' : item.uncapped_flag === '0' ? 'Capped' : '';
           default:
               return item[colId] || '';
       }
   };

   const handleDownload = () => {
       const headers = columns.map(col => col.label);
       const rows = filteredData.map(item =>
           columns.map(col => getCellValue(item, col.id))
       );
       downloadCSV(`amzl_ds_inputs_${selectedDate || 'all'}.csv`, headers, rows);
   };

   const columns = [
       { id: 'ofd_date', label: 'OFD Date' },
       { id: 'location_id', label: 'Node' },
       { id: 'cycle_name', label: 'Cycle' },
       { id: 'utr', label: 'UTR' },
       { id: 'mnr', label: 'MNR' },
       { id: 'source', label: 'Source' },
       { id: 'cycle_transfer', label: 'Cycle Transfer' },
       { id: 'earlies', label: 'Earlies' },
       { id: 'backlog', label: 'Backlog' },
       { id: 'backlog_reason', label: 'Backlog Reason' },
       { id: 'uncapped_flag', label: 'Uncap' },
       { id: 'last_updated_by', label: 'Updated By' },
       { id: 'last_updated_at', label: 'Last Updated At' },
   ];

   return (
       <Column spacing="400">
           <Heading level={4} type="h300">AMZL DS Inputs</Heading>

           <TabGroup value={activeTab} onChange={setActiveTab}>
               <Tab value="cap-setting-inputs">Cap Setting Inputs</Tab>
               <Tab value="tactical-caps">Tactical Caps</Tab>
           </TabGroup>

           {activeTab === 'cap-setting-inputs' && (
           <Column spacing="400">
            <Alert type="neutral" size="medium">
               Please use this page to provide UTR, MNR, Cycle Transfer, Backlog, and Uncap.
           </Alert>


           {error && (
                <Alert type="error" size="medium" onClose={() => setError(null)}>
                   {error}
               </Alert>
           )}

           {isLoading && (
               <Row alignmentHorizontal="center" spacingInset="450">
                    <Loader size="small" />
               </Row>
           )}

           {!isLoading && (
               <Column width="100%">
                   {/* Bespoke Table Action Bar */}
                   <Column type="fill" spacing="300">
                       <Row
                           spacingInset="300 none"
                           width="100%"
                           widths="fit"
                           alignmentHorizontal="justify"
                       >
                           <Row spacing="200" spacingInset="none 300 none">
                               <Select
                                   value={selectedDate}
                                   onChange={setSelectedDate}
                                   placeholder="OFD Date"
                                   size="small"
                                   disabled={isLoading}
                                   width={150}
                               >
                                   {dateOptions.map(date => (
                                       <SelectOption key={date} value={date} label={date} />
                                   ))}
                               </Select>
                               <Select
                                   value={selectedParentNode}
                                   onChange={setSelectedParentNode}
                                   placeholder="Parent Node"
                                   size="small"
                                   disabled={isLoading || !selectedDate}
                                   width={150}
                               >
                                   {parentNodeOptions.map(node => (
                                       <SelectOption key={node} value={node} label={node} />
                                   ))}
                               </Select>
                               <SearchField
                                   value={filterText}
                                   onChange={setFilterText}
                                   width={180}
                                   placeholder="Search"
                                   size="small"
                                   onSubmit={() => {}}
                               />
                           </Row>
                           <Row spacing="200" spacingInset="none 300 none">
                               <Button type={selectedParentNode ? "primary" : "secondary"} size="small" onClick={openEditModal} disabled={!selectedParentNode}>
                                   Update
                               </Button>
                               <Button type="secondary" size="small" onClick={handleDownload} disabled={filteredData.length === 0}>
                                   Download
                               </Button>
                               <Button type="secondary" size="small" onClick={() => { setSelectedDate(''); setSelectedParentNode(''); setFilterText(''); loadData(); }} disabled={isLoading}>
                                   Refresh
                               </Button>
                           </Row>
                       </Row>
                       <Divider />
                   </Column>

                    {/* Data Table */}
                    <DataTable
                        onSort={onSort}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                    >
                        <DataTableHead>
                            <DataTableRow highlightOnHover={false}>
                                {columns.map(col => (
                                    <DataTableHeader key={col.id} sortColumn={col.id}>{col.label}</DataTableHeader>
                                ))}
                            </DataTableRow>
                        </DataTableHead>
                        <DataTableBody>
                            {paginatedData.map((item, idx) => (
                                <DataTableRow key={getRowKey(item) + '-' + idx} highlightOnHover={false}>
                                    <DataTableCell>{item.ofd_date}</DataTableCell>
                                    <DataTableCell>{item.location_id}</DataTableCell>
                                    <DataTableCell>{item.cycle_name}</DataTableCell>
                                    <DataTableCell>{item.utr}</DataTableCell>
                                    <DataTableCell>{item.cycle_name === 'CYCLE_1' ? item.mnr : ''}</DataTableCell>
                                    <DataTableCell>{item.source}</DataTableCell>
                                    <DataTableCell>{item.cycle_transfer}</DataTableCell>
                                    <DataTableCell>{item.earlies}</DataTableCell>
                                    <DataTableCell>{item.cycle_name === 'CYCLE_1' ? item.backlog : ''}</DataTableCell>
                                    <DataTableCell>{item.cycle_name === 'CYCLE_1' ? item.backlog_reason : ''}</DataTableCell>
                                    <DataTableCell>
                                        {item.uncapped_flag === '1' ? 'Uncapped' : item.uncapped_flag === '0' ? 'Capped' : ''}
                                    </DataTableCell>
                                    <DataTableCell>{item.last_updated_by}</DataTableCell>
                                    <DataTableCell>{item.last_updated_at || ''}</DataTableCell>
                                </DataTableRow>
                            ))}
                            {paginatedData.length === 0 && (
                                <DataTableRow highlightOnHover={false}>
                                    <DataTableCell colSpan={columns.length}>
                                        <Row alignmentHorizontal="center" spacingInset="400">
                                            <Text color="secondary">No data available</Text>
                                        </Row>
                                    </DataTableCell>
                                </DataTableRow>
                            )}
                        </DataTableBody>
                    </DataTable>

                   {/* Bespoke Table Footer */}
                   <Row
                       type="fill"
                       spacingInset="300"
                       width="100%"
                       widths="fit"
                       alignmentHorizontal="justify"
                   >
                       <Text type="b200" color="secondary">{filteredData.length} records</Text>
                       <Row spacing="200" spacingInset="none 300 none">
                           <Text tag="label" type="b200" color="primary" htmlFor="results-select">
                               Results per page
                           </Text>
                           <Select
                               id="results-select"
                               value={resultsPerPage}
                               onChange={setResultsPerPage}
                               size="small"
                           >
                               <SelectOption value={10} label="10" />
                               <SelectOption value={20} label="20" />
                               <SelectOption value={50} label="50" />
                           </Select>
                           {totalPages > 1 && (
                               <Pagination
                                   numberOfPages={totalPages}
                                   currentPage={currentPage}
                                   onChange={setCurrentPage}
                               />
                           )}
                       </Row>
                   </Row>
               </Column>
           )}

           {/* Edit Modal */}
           <Modal
               title={`Update DS Inputs — ${selectedParentNode} (${selectedDate})`}
               open={isModalOpen}
               onClose={() => setIsModalOpen(false)}
               scrollContainer="modal"
               width="1200px"
           >
               <Table headerRows={1} showDividers={true} spacing="small">
                   <TableRow>
                       <TableCell>Node</TableCell>
                       <TableCell>Cycle</TableCell>
                       <TableCell>UTR</TableCell>
                       <TableCell>MNR</TableCell>
                       <TableCell>Source</TableCell>
                       <TableCell>Cycle Transfer</TableCell>
                       <TableCell>Earlies</TableCell>
                       <TableCell>Backlog</TableCell>
                       <TableCell>Backlog Reason</TableCell>
                       <TableCell>Uncap</TableCell>
                   </TableRow>
                   {editData.map((item, idx) => {
                       const errors = getModalValidationError(item);
                       const nodeInfo = nodeConfig.find((nc: any) => nc.location_id === item.location_id);
                       const nodeType = nodeInfo?.type || '';
                       const nodeParent = nodeInfo?.parent || '';
                       const isParentNode = item.location_id === nodeParent;
                       const isUtrDisabled = nodeType === 'AMZL_XPT' || (nodeType === 'AMZL' && !isParentNode);
                       return (
                           <TableRow key={`edit-${item.location_id}-${item.cycle_name}-${idx}`}>
                               <TableCell>{item.location_id}</TableCell>
                               <TableCell>{item.cycle_name}</TableCell>
                               <TableCell>
                                   <Input
                                       value={item.utr || ''}
                                       onChange={(val) => handleModalEdit(idx, 'utr', val)}
                                       type="number"
                                       size="small"
                                       disabled={isUtrDisabled}
                                   />
                               </TableCell>
                               <TableCell>
                                   <Input
                                       value={item.cycle_name === 'CYCLE_1' ? (item.mnr || '') : ''}
                                       onChange={(val) => handleModalEdit(idx, 'mnr', val)}
                                       type="number"
                                       size="small"
                                       disabled={item.cycle_name !== 'CYCLE_1'}
                                   />
                               </TableCell>
                               <TableCell>
                                   <Input
                                       value={item.source || ''}
                                       onChange={(val) => handleModalEdit(idx, 'source', val)}
                                       size="small"
                                       error={errors.source}
                                       placeholder={errors.source ? 'Required' : ''}
                                       disabled={item.cycle_name !== 'CYCLE_1'}
                                   />
                               </TableCell>
                               <TableCell>
                                   <Input
                                       value={item.cycle_transfer || ''}
                                       onChange={(val) => handleModalEdit(idx, 'cycle_transfer', val)}
                                       type="number"
                                       size="small"
                                   />
                               </TableCell>
                               <TableCell>
                                   <Input
                                       value={item.earlies || ''}
                                       onChange={(val) => handleModalEdit(idx, 'earlies', val)}
                                       type="number"
                                       size="small"
                                   />
                               </TableCell>
                               <TableCell>
                                   <Input
                                       value={item.cycle_name === 'CYCLE_1' ? (item.backlog || '') : ''}
                                       onChange={(val) => handleModalEdit(idx, 'backlog', val)}
                                       type="number"
                                       size="small"
                                       disabled={item.cycle_name !== 'CYCLE_1'}
                                   />
                               </TableCell>
                               <TableCell>
                                   <Select
                                       value={item.cycle_name === 'CYCLE_1' ? (item.backlog_reason || '') : ''}
                                       onChange={(val) => handleModalEdit(idx, 'backlog_reason', val)}
                                       size="small"
                                       disabled={item.cycle_name !== 'CYCLE_1'}
                                       error={errors.backlog_reason}
                                       placeholder={errors.backlog_reason ? 'Required' : 'Select reason'}
                                   >
                                       {BACKLOG_REASONS.map(reason => (
                                           <SelectOption key={reason} value={reason} label={reason} />
                                       ))}
                                   </Select>
                               </TableCell>
                               <TableCell>
                                   <Toggle
                                       checked={String(item.uncapped_flag) === '1'}
                                       onChange={() => handleModalEdit(
                                           idx, 'uncapped_flag',
                                           String(item.uncapped_flag) === '1' ? '0' : '1'
                                       )}
                                   />
                               </TableCell>
                           </TableRow>
                       );
                   })}
               </Table>
               <ModalFooter>
                   <Row alignmentHorizontal="end" spacing="300">
                        <Button type="tertiary" size="small" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button type="primary" size="small" onClick={handleSaveAll} disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                   </Row>
               </ModalFooter>
           </Modal>
           </Column>
           )}

           {activeTab === 'tactical-caps' && (
               <TacticalCaps />
           )}
       </Column>
   );
};

export default AMZLDSInputs;
